import type { RequestOptions } from "../common/server";
import type { HistoryListingJs, ListJs, PluginLocation, RedirectJs, ResponseObject, SeoJs, ShortcutPluginInfo } from "@/generated/models";
import type { VarsJsOf } from "@/services/models/responseObject";
import { objectToFormData } from "@/services/utils/formData";
import { server } from "./server";

interface AllowOnDemandModel {
  shortcut: string;
  allow: boolean;
}
export function allowOnDemand({ shortcut, allow }: { shortcut: string; allow: boolean }) {
  const model: AllowOnDemandModel = {
    shortcut,
    allow,
  };
  const formData = objectToFormData(model);
  return server.post("sites/allowOnDemand", { body: formData });
}

enum SortByColumn {
  RevisionNumber,
  User,
}

export async function getHistory(shortcut: string, pagination: {
  descending?: boolean;
  rowsPerPage?: number;
  startingResult?: number;
  sortBy?: number;
  totalItems?: number;
  beforeRevision?: number;
  untilTime?: Date;
} = {}, { signal }: RequestOptions = {}) {
  const {
    startingResult,
    rowsPerPage: resultsPerPage = 0,
    sortBy: sortByColumnNum = SortByColumn.RevisionNumber,
    descending: sortDescending = true,
    beforeRevision = 0,
    untilTime,
  } = pagination;

  const searchParams = new URLSearchParams({
    shortcut,
    resultsPerPage: resultsPerPage.toString(),
    sortByColumnNum: sortByColumnNum.toString(),
    sortDescending: sortDescending.toString(),
    beforeRevision: beforeRevision.toString(),
  });

  if (startingResult !== undefined) {
    searchParams.append("startingResult", startingResult.toString());
  }
  if (untilTime) {
    searchParams.append("untilTime", untilTime.toISOString());
  }

  const response = await server.get("sites/getHistory", {
    searchParams,
    signal,
  });

  return await response.json() as HistoryListingJs;
}

export async function getPluginInfo(shortcut: string, { signal }: RequestOptions = {}) {
  const response = await server.get("sites/getPluginInfo", {
    searchParams: {
      shortcut,
    },
    signal,
  });

  return await response.json() as ShortcutPluginInfo;
}

export interface PluginModel {
  PluginName: string;
  PluginPriority: number;
  PluginLocation: PluginLocation;
  PluginContent: string;
  PluginMappingId: string;
  ElementLabel?: string | null;
}

export interface AddPluginResponse
  extends VarsJsOf<{
    newShortcutPluginId: string;
  }> {}

export async function addPlugin(shortcut: string, model: Omit<PluginModel, "PluginMappingId">) {
  const formData = objectToFormData({ shortcut, ...model });
  const response = await server.post("sites/addPlugin", { body: formData });

  return await response.json() as AddPluginResponse;
}

export async function editPlugin(shortcut: string, model: PluginModel) {
  const formData = objectToFormData({ shortcut, ...model });
  const response = await server.post("sites/editPlugin", { body: formData });

  return await response.json() as ResponseObject;
}

export async function getRedirects(shortcut: string, { signal }: RequestOptions = {}) {
  const response = await server.get("sites/getRedirects", {
    searchParams: {
      shortcut,
    },
    signal,
  });

  return await response.json() as ListJs<RedirectJs>;
}

export async function getPrettyUrls(shortcut: string, { signal }: RequestOptions = {}) {
  const response = await server.get("sites/getSeoInfo", {
    searchParams: {
      shortcut,
    },
    signal,
  });

  return await response.json() as ListJs<SeoJs>;
}

export interface EditSeoJs {
  pageInfoIds: string[];
  pageInfoTitles: string[];
  pageInfoUrls: string[];
  pageInfoDescriptions: string[];
}

export async function editPrettyUrl(shortcut: string, model: EditSeoJs) {
  const formData = objectToFormData({ shortcut, ...model });
  const response = await server.post("sites/editSeoInfo", { body: formData });

  return await response.json() as SeoJs;
}

export async function setRootPage(shortcut: string, rootPage: string, clearRoot: boolean) {
  const formData = objectToFormData({ shortcut, rootPage, clearRoot });
  const response = await server.post("sites/setRootPage", { body: formData });

  return await response.json() as ResponseObject;
}

export async function set404Page(shortcut: string, notFoundPage: string, clear404: boolean) {
  const formData = objectToFormData({ shortcut, notFoundPage, clear404 });
  const response = await server.post("sites/set404page", { body: formData });

  return await response.json() as ResponseObject;
}

export interface RedirectModel {
  frompath: string;
  topath: string;
  redirectId?: string;
}

export async function addRedirect(shortcut: string, model: RedirectModel) {
  const formData = objectToFormData({ shortcut, ...model });
  const response = await server.post("sites/addRedirect", { body: formData });

  return await response.json() as RedirectJs;
}

export async function editRedirect(shortcut: string, model: RedirectModel) {
  const formData = objectToFormData({ shortcut, ...model });
  const response = await server.post("sites/editRedirect", { body: formData });

  return await response.json() as RedirectJs;
}

export async function deleteRedirect(shortcut: string, redirectId: string) {
  const formData = objectToFormData({
    shortcut,
    RedirectId: redirectId,
  });
  const response = await server.post("sites/deleteRedirect", { body: formData });
  return await response.json() as ResponseObject;
}

export interface PluginPagesModel {
  PagesToInclude?: string[];
  PagesToExclude?: string[];
  PluginMappingId: string;
  InsertAllPages: boolean;
}

export async function editPluginPages(shortcut: string, model: PluginPagesModel) {
  const formData = objectToFormData({
    shortcut,
    ...model,
  });

  const response = await server.post("sites/editPluginPages", { body: formData });
  return await response.json() as ResponseObject;
}

export async function deletePlugin(shortcut: string, pluginMappingId: string) {
  const formData = objectToFormData({
    shortcut,
    PluginMappingId: pluginMappingId,
  });
  const result = await server.post("sites/deletePlugin", { body: formData });
  return await result.json() as ResponseObject;
}
